import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { plansAPI, jobsAPI } from '@/utils/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { QRCodeSVG } from 'qrcode.react';
import { PromoCountdown } from '@/components/PromoCountdown';
import { EurHint } from '@/components/EurHint';
import {
  Star, ShieldCheck, Crown, CheckCircle2, Briefcase, Zap,
  CreditCard, Building2, Upload, ArrowLeft, Copy, Check, QrCode, Globe2, Loader2, XCircle, Tag
} from 'lucide-react';

const ICON_MAP = { simple: Briefcase, featured: Star, pack_5: ShieldCheck, pack_15: Crown, free: Zap };
const COLOR_MAP = {
  simple:   { bg: 'from-blue-50 to-blue-100',   ring: 'ring-blue-300',   text: 'text-blue-700' },
  featured: { bg: 'from-amber-50 to-amber-100', ring: 'ring-amber-400',  text: 'text-amber-700' },
  pack_5:   { bg: 'from-orange-50 to-orange-100', ring: 'ring-orange-400', text: 'text-orange-700' },
  pack_15:  { bg: 'from-purple-50 to-purple-100', ring: 'ring-purple-400', text: 'text-purple-700' },
};

export default function Checkout() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const initialPlan = params.get('plan');
  const stripeSessionId = params.get('session_id');
  const stripeCancelled = params.get('stripe') === 'cancelled';
  const republishJobId = params.get('republish'); // job to auto-publish after payment succeeds
  const [plans, setPlans] = useState([]);
  const [config, setConfig] = useState(null);
  const [selected, setSelected] = useState(initialPlan || null);
  const [paymentMethod, setPaymentMethod] = useState('vinti4');
  const [step, setStep] = useState(stripeSessionId ? 'stripe-return' : 'select'); // select | bank | stripe-return | done
  const [purchase, setPurchase] = useState(null);
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [proofFile, setProofFile] = useState(null);
  const [copied, setCopied] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [promoCode, setPromoCode] = useState(() => (localStorage.getItem('trabadju_ref') || '').toUpperCase());
  const [appliedCode, setAppliedCode] = useState(() => (localStorage.getItem('trabadju_ref') || '').toUpperCase());
  const [quote, setQuote] = useState(null);
  const [quoteError, setQuoteError] = useState('');
  const [quoting, setQuoting] = useState(false);
  const [autoApplied, setAutoApplied] = useState(() => Boolean(localStorage.getItem('trabadju_ref')));
  const [stripeResult, setStripeResult] = useState(null); // { status, payment_status, ... }
  const [republishDone, setRepublishDone] = useState(false);

  useEffect(() => {
    Promise.all([plansAPI.getPlans(), plansAPI.getPaymentConfig()])
      .then(([p, c]) => { setPlans(p.data.filter(x => x.plan_type !== 'free')); setConfig(c.data); })
      .catch(() => setMessage(t('checkout.errorLoadPlans')));
  }, [t]);

  // Price quote (promotions + loyalty + optional code) for the selected plan.
  useEffect(() => {
    if (!selected) { setQuote(null); return; }
    let cancelled = false;
    setQuoting(true);
    if (appliedCode) setQuoteError('');
    const payload = { plan_type: selected };
    if (appliedCode) payload.promo_code = appliedCode;
    if (republishJobId) payload.republish_job_id = republishJobId;
    plansAPI.quote(payload)
      .then(({ data }) => { if (!cancelled) setQuote(data); })
      .catch((e) => {
        if (cancelled) return;
        setQuote(null);
        if (autoApplied) {
          // Referral code from ?ref= is not (or no longer) a valid promo — drop it silently.
          setAutoApplied(false);
          setPromoCode('');
        } else {
          setQuoteError(e?.response?.data?.detail || t('checkout.promoInvalid'));
        }
        if (appliedCode) setAppliedCode('');
      })
      .finally(() => { if (!cancelled) setQuoting(false); });
    return () => { cancelled = true; };
  }, [selected, appliedCode, republishJobId, t]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!stripeSessionId) return;
    let attempts = 0;
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      try {
        const { data } = await plansAPI.stripeStatus(stripeSessionId);
        setStripeResult(data);
        const done = data.payment_status === 'paid' || data.status === 'expired' || data.status === 'complete';
        if (done) return;
      } catch (e) {
        setMessage(e?.response?.data?.detail || t('checkout.errorCheckPayment'));
        return;
      }
      attempts += 1;
      if (attempts < 10) {
        setTimeout(poll, 2000);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [stripeSessionId, t]);

  // Auto-republish a job after the payment is confirmed (works for both Stripe + Vinti4).
  // Vinti4 lands on /payment-result, but if user reaches /checkout/stripe-return with paid status
  // OR comes back to /checkout with ?republish=<id> and already has credits, we trigger publish here.
  useEffect(() => {
    if (!republishJobId || republishDone) return;
    const paid = stripeResult?.payment_status === 'paid' || stripeResult?.purchase_status === 'approved';
    if (!paid) return;
    (async () => {
      try {
        await jobsAPI.publish(republishJobId);
        setRepublishDone(true);
      } catch (e) {
        // Surface error but don't block — employer can retry from MyJobs
        const detail = e?.response?.data?.detail || '';
        setMessage(t('checkout.paidButRepublishFailed', { detail }));
      }
    })();
  }, [republishJobId, republishDone, stripeResult, t]);

  const handleConfirmPlan = async () => {
    if (!selected) return;
    setSubmitting(true);
    setMessage('');
    try {
      const payload = { plan_type: selected, payment_method: paymentMethod };
      if (republishJobId) payload.republish_job_id = republishJobId;
      if (appliedCode && quote?.code_valid) payload.promo_code = appliedCode;
      const { data } = await plansAPI.createPurchase(payload);
      setPurchase(data);
      if (data.status === 'approved') {
        // 100% de desconto — créditos activados sem pagamento
        setStripeResult({ payment_status: 'paid', purchase_status: 'approved', invoice_number: data.invoice_number });
        setStep('stripe-return');
        return;
      }
      if (paymentMethod === 'vinti4') {
        // Full-page navigation to the backend redirect endpoint, which renders
        // an auto-submit HTML form POSTing to SISP's 3ds_init.php.
        const apiBase = process.env.REACT_APP_BACKEND_URL;
        window.location.href = `${apiBase}/api/purchases/${data.id}/vinti4/redirect`;
        return;
      }
      if (paymentMethod === 'stripe') {
        // Ask backend to create a Stripe Checkout Session, then redirect.
        const { data: s } = await plansAPI.stripeCheckout(data.id, window.location.origin);
        if (!s?.url) throw new Error(t('checkout.errorStripeUrlMissing'));
        window.location.href = s.url;
        return;
      }
      setStep('bank');
    } catch (e) {
      setMessage(e?.response?.data?.detail || e?.message || t('checkout.errorStartPurchase'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleUploadProof = async () => {
    if (!proofFile || !purchase) return;
    setSubmitting(true);
    try {
      await plansAPI.uploadProof(purchase.id, proofFile);
      setStep('done');
    } catch (e) {
      setMessage(e?.response?.data?.detail || t('checkout.errorUploadProof'));
    } finally {
      setSubmitting(false);
    }
  };

  const copyToClipboard = (text, key) => {
    navigator.clipboard?.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(''), 2000);
  };

  const selectedPlan = plans.find(p => p.plan_type === selected);

  if (step === 'stripe-return') {
    const paid = stripeResult?.payment_status === 'paid' || stripeResult?.purchase_status === 'approved';
    const stillProcessing = !stripeResult || (stripeResult.payment_status === 'unpaid' && stripeResult.status !== 'expired');
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-2xl mx-auto px-4">
          <Card className="p-10 text-center" data-testid="stripe-return-panel">
            {paid ? (
              <>
                <CheckCircle2 className="h-16 w-16 text-emerald-500 mx-auto mb-4" />
                <h1 className="text-3xl font-bold mb-3">{t('checkout.paymentConfirmed')}</h1>
                <p className="text-gray-600 mb-6">
                  {t('checkout.invoiceLabel')} <strong>{stripeResult?.invoice_number}</strong> · {t('checkout.creditsActive')}
                </p>
                {republishJobId && (
                  <p className="text-sm text-emerald-700 bg-emerald-50 rounded p-3 mb-4" data-testid="stripe-republish-status">
                    {republishDone ? `✅ ${t('checkout.jobRepublishedAuto')}` : `⏳ ${t('checkout.republishing')}`}
                  </p>
                )}
                <div className="flex gap-3 justify-center flex-wrap">
                  <Button onClick={() => navigate(republishJobId ? '/my-jobs' : '/dashboard')} className="bg-blue-600 hover:bg-blue-700" data-testid="stripe-done-dashboard">
                    {republishJobId ? t('checkout.viewMyJobs') : t('checkout.goToDashboard')}
                  </Button>
                  <Button variant="outline" onClick={() => navigate('/purchases')} data-testid="stripe-done-history">
                    {t('checkout.viewHistory')}
                  </Button>
                </div>
              </>
            ) : stillProcessing ? (
              <>
                <Loader2 className="h-12 w-12 text-blue-600 mx-auto mb-4 animate-spin" />
                <h1 className="text-2xl font-bold mb-2">{t('checkout.confirmingPayment')}</h1>
                <p className="text-gray-600">{t('checkout.validatingStripe')}</p>
              </>
            ) : (
              <>
                <XCircle className="h-14 w-14 text-red-500 mx-auto mb-4" />
                <h1 className="text-2xl font-bold mb-3">{t('checkout.paymentNotCompleted')}</h1>
                <p className="text-gray-600 mb-6">
                  {t('checkout.sessionExpiredOrCancelled')}
                </p>
                <Button onClick={() => navigate('/checkout')} className="bg-blue-600 hover:bg-blue-700" data-testid="stripe-retry">
                  {t('checkout.tryAgain')}
                </Button>
              </>
            )}
            {message && <Alert variant="destructive" className="mt-6"><AlertDescription>{message}</AlertDescription></Alert>}
          </Card>
        </div>
      </div>
    );
  }

  if (step === 'done') {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-2xl mx-auto px-4">
          <Card className="p-10 text-center">
            <CheckCircle2 className="h-16 w-16 text-emerald-500 mx-auto mb-4" />
            <h1 className="text-3xl font-bold mb-3">{t('checkout.proofReceived')}</h1>
            <p className="text-gray-600 mb-2">
              {t('checkout.invoiceLabel')} <strong>{purchase?.invoice_number}</strong> · <strong>{purchase?.plan_name}</strong> · <strong>{purchase?.amount_cve?.toLocaleString('pt-PT')} CVE</strong>
            </p>
            <p className="text-gray-600 mb-6">
              {t('checkout.proofSubmittedBody')}
            </p>
            <div className="flex gap-3 justify-center flex-wrap">
              <Button onClick={() => navigate('/dashboard')} className="bg-blue-600 hover:bg-blue-700" data-testid="checkout-done-dashboard">
                {t('checkout.backToDashboard')}
              </Button>
              <Button variant="outline" onClick={() => navigate('/purchases')} data-testid="checkout-done-history">
                {t('checkout.viewHistory')}
              </Button>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  if (step === 'bank' && purchase) {
    const bank = config?.bank || {};
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-3xl mx-auto px-4">
          <Button variant="ghost" onClick={() => setStep('select')} className="mb-4">
            <ArrowLeft className="h-4 w-4 mr-2" />{t('checkout.back')}
          </Button>
          <Card className="p-8" data-testid="bank-transfer-panel">
            <div className="flex items-center gap-3 mb-6">
              <Building2 className="h-7 w-7 text-blue-600" />
              <div>
                <h1 className="text-2xl font-bold">{t('checkout.bankTransferTitle')}</h1>
                <p className="text-gray-600 text-sm">{t('checkout.invoiceLabel')} <strong>{purchase.invoice_number}</strong> · {purchase.plan_name}</p>
              </div>
            </div>

            <Card className="p-5 bg-blue-50 border-blue-200 mb-6">
              <div className="flex justify-between items-baseline mb-3">
                <span className="text-sm text-gray-600">{t('checkout.amountToTransfer')}</span>
                <span className="text-3xl font-bold text-blue-700">{purchase.amount_cve.toLocaleString('pt-PT')} CVE</span>
                <EurHint cve={purchase.amount_cve} className="text-gray-500" testId="purchase-amount-eur" />
              </div>
              <DataRow label={t('checkout.accountHolder')} value={bank.holder} onCopy={() => copyToClipboard(bank.holder, 'holder')} copied={copied === 'holder'} t={t} />
              {bank.bank_name && <DataRow label={t('checkout.bankName')} value={bank.bank_name} onCopy={() => copyToClipboard(bank.bank_name, 'bank')} copied={copied === 'bank'} t={t} />}
              {bank.account_number && <DataRow label={t('checkout.accountNumber')} value={bank.account_number} onCopy={() => copyToClipboard(bank.account_number, 'acc')} copied={copied === 'acc'} t={t} />}
              {bank.nib && <DataRow label="NIB" value={bank.nib} onCopy={() => copyToClipboard(bank.nib, 'nib')} copied={copied === 'nib'} t={t} />}
              <DataRow
                label="IBAN"
                value={bank.iban || bank.iban_placeholder || t('checkout.ibanComingSoon')}
                placeholder={!bank.iban}
                onCopy={bank.iban ? () => copyToClipboard(bank.iban, 'iban') : null}
                copied={copied === 'iban'}
                t={t}
              />
              {bank.swift && <DataRow label="SWIFT/BIC" value={bank.swift} onCopy={() => copyToClipboard(bank.swift, 'swift')} copied={copied === 'swift'} t={t} />}
              <DataRow
                label={t('checkout.reference')}
                value={purchase.bank_reference}
                highlight
                onCopy={() => copyToClipboard(purchase.bank_reference, 'ref')}
                copied={copied === 'ref'}
                t={t}
              />
              <p className="text-xs text-amber-700 bg-amber-50 p-2 rounded mt-3">
                <strong>{t('checkout.important')}:</strong> {t('checkout.referenceWarning')} <code className="font-mono">{purchase.bank_reference}</code> {t('checkout.referenceWarningSuffix')}
              </p>
            </Card>

            {/* QR Code de pagamento — alternativa mobile */}
            {(() => {
              // QR contém um URL para a página pública de pagamento.
              // Ao ler com a câmara, o telemóvel abre uma página polida com botões para copiar
              // cada campo. Resolve o problema de a câmara interpretar os dados como nº de telefone.
              const baseUrl = window.location.origin;
              const qrPayload = `${baseUrl}/pay/${purchase.invoice_number}`;
              return (
                <Card className="p-5 bg-white border-2 border-blue-100 mb-6" data-testid="payment-qr-card">
                  <div className="flex flex-col sm:flex-row items-center gap-5">
                    <div className="flex-shrink-0 p-3 bg-white rounded-lg border border-gray-200">
                      <QRCodeSVG
                        value={qrPayload}
                        size={170}
                        level="M"
                        includeMargin={false}
                        data-testid="payment-qr-svg"
                      />
                    </div>
                    <div className="flex-1 text-center sm:text-left">
                      <div className="flex items-center gap-2 mb-2 justify-center sm:justify-start">
                        <QrCode className="h-5 w-5 text-blue-600" />
                        <h3 className="font-semibold text-gray-900">{t('checkout.quickScanTitle')}</h3>
                      </div>
                      <p className="text-sm text-gray-600 mb-3">
                        {t('checkout.quickScanBody')}
                      </p>
                      <p className="text-xs text-gray-500">
                        {t('checkout.quickScanHint')} <span className="font-mono text-blue-700 break-all">{qrPayload}</span>
                      </p>
                    </div>
                  </div>
                </Card>
              );
            })()}

            <h3 className="font-semibold text-gray-900 mb-3">{t('checkout.sendProofTitle')}</h3>
            <p className="text-sm text-gray-600 mb-3">{t('checkout.sendProofBody')}</p>
            <div className="flex flex-col gap-3">
              <label className="flex items-center gap-3 border-2 border-dashed border-gray-300 rounded-lg p-6 cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors">
                <Upload className="h-6 w-6 text-gray-500" />
                <div className="flex-1">
                  <div className="font-medium text-gray-900">
                    {proofFile ? proofFile.name : t('checkout.clickToChooseFile')}
                  </div>
                  <div className="text-xs text-gray-500">{t('checkout.fileHint')}</div>
                </div>
                <input
                  type="file"
                  className="hidden"
                  accept="application/pdf,image/*"
                  onChange={(e) => setProofFile(e.target.files?.[0] || null)}
                  data-testid="proof-file-input"
                />
              </label>
              {message && <Alert variant="destructive"><AlertDescription>{message}</AlertDescription></Alert>}
              <Button onClick={handleUploadProof} disabled={!proofFile || submitting} className="bg-blue-600 hover:bg-blue-700" data-testid="submit-proof-btn">
                {submitting ? t('checkout.sending') : t('checkout.sendProof')}
              </Button>
              <Button variant="outline" onClick={() => navigate('/purchases')}>
                {t('checkout.sendLater')}
              </Button>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-5xl mx-auto px-4">
        <Button variant="ghost" onClick={() => navigate('/dashboard')} className="mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" />{t('checkout.backToDashboardFull')}
        </Button>
        <h1 className="text-4xl font-bold text-gray-900 mb-2">{t('checkout.title')}</h1>
        <p className="text-gray-600 mb-8">{t('checkout.subtitle')}</p>

        {message && <Alert variant="destructive" className="mb-6"><AlertDescription>{message}</AlertDescription></Alert>}
        {stripeCancelled && (
          <Alert className="mb-6 border-amber-300 bg-amber-50">
            <AlertDescription className="text-amber-800" data-testid="stripe-cancelled-alert">
              {t('checkout.stripeCancelled')}
            </AlertDescription>
          </Alert>
        )}

        <PromoCountdown plans={plans} showCta={false} className="mb-8" />

        {/* Plans */}
        <h2 className="text-sm uppercase tracking-widest text-gray-500 mb-3">{t('checkout.step1Plan')}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {plans.map((p) => {
            const Icon = ICON_MAP[p.plan_type] || Briefcase;
            const colors = COLOR_MAP[p.plan_type] || COLOR_MAP.simple;
            const active = selected === p.plan_type;
            const promo = p.promo;
            const promoTag = promo
              ? (promo.kind === 'percent' ? `-${promo.value}%` : promo.kind === 'fixed' ? `-${promo.value.toLocaleString('pt-PT')} CVE` : `+${promo.value} ${t('checkout.promoBonusShort')}`)
              : null;
            return (
              <button
                key={p.plan_type}
                type="button"
                onClick={() => { setSelected(p.plan_type); setQuoteError(''); }}
                className={`relative text-left p-5 rounded-xl bg-gradient-to-br ${colors.bg} ring-2 transition-all ${
                  active ? `${colors.ring} shadow-lg scale-[1.02]` : 'ring-transparent hover:ring-gray-300'
                }`}
                data-testid={`checkout-plan-${p.plan_type}`}
              >
                {promoTag && (
                  <span className="absolute top-3 right-3 px-2 py-0.5 rounded-full bg-rose-600 text-white text-[11px] font-bold" data-testid={`checkout-promo-${p.plan_type}`}>
                    {promoTag}
                  </span>
                )}
                {!promoTag && p.label && (
                  <span className="absolute top-3 right-3 px-2 py-0.5 rounded-full bg-slate-900 text-white text-[11px] font-bold">
                    {p.label}
                  </span>
                )}
                <div className={`w-10 h-10 rounded-full bg-white flex items-center justify-center mb-3 ${colors.text}`}>
                  <Icon className="h-5 w-5" />
                </div>
                <div className={`font-bold ${colors.text}`}>{p[`name_${i18n.language}`] || p.name_pt}</div>
                <div className="text-2xl font-bold text-gray-900 my-2">
                  {promo && promo.saving_cve > 0 && (
                    <span className="block text-sm font-medium text-gray-400 line-through">{p.price_cve.toLocaleString('pt-PT')} CVE</span>
                  )}
                  {(promo && promo.saving_cve > 0 ? promo.promo_price_cve : p.price_cve).toLocaleString('pt-PT')} <span className="text-sm text-gray-600">CVE</span>
                  <EurHint cve={promo && promo.saving_cve > 0 ? promo.promo_price_cve : p.price_cve} className="text-gray-500" testId={`plan-eur-${p.plan_type}`} />
                </div>
                <ul className="space-y-1 text-xs text-gray-700">
                  {p.features_pt.slice(0, 3).map((f, i) => (
                    <li key={i} className="flex items-start">
                      <CheckCircle2 className={`h-3.5 w-3.5 mr-1 mt-0.5 ${colors.text} flex-shrink-0`} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </button>
            );
          })}
        </div>

        {/* Payment method */}
        <h2 className="text-sm uppercase tracking-widest text-gray-500 mb-3">{t('checkout.step2Payment')}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <PaymentMethodCard
            active={paymentMethod === 'vinti4'}
            disabled={!config?.vinti4_enabled}
            onClick={() => config?.vinti4_enabled && setPaymentMethod('vinti4')}
            icon={CreditCard}
            title={t('checkout.vinti4Title')}
            subtitle={config?.vinti4_enabled ? t('checkout.vinti4Subtitle') : t('checkout.comingSoonSisp')}
            badge={!config?.vinti4_enabled ? t('checkout.comingSoonBadge') : null}
            testId="payment-method-vinti4"
          />
          <PaymentMethodCard
            active={paymentMethod === 'stripe'}
            disabled={!config?.stripe_enabled}
            onClick={() => config?.stripe_enabled && setPaymentMethod('stripe')}
            icon={Globe2}
            title={t('checkout.internationalCardTitle')}
            subtitle={config?.stripe_enabled ? t('checkout.internationalCardSubtitle') : t('checkout.unavailable')}
            badge={config?.stripe_enabled ? t('checkout.recommendedDiaspora') : null}
            testId="payment-method-stripe"
          />
        </div>

        {/* Accepted brand logos + trust bar (SISP checklist item 2 + item 5) */}
        <div className="mb-8 bg-white border border-gray-200 rounded-xl px-5 py-4" data-testid="accepted-cards-strip">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex flex-wrap items-center gap-3 sm:gap-4">
              <span className="text-xs uppercase tracking-widest text-gray-500 font-semibold">
                {t('checkout.acceptedCards')}
              </span>
              <div className="flex flex-wrap items-center gap-3">
                <img src="/payment-logos/vinti4.png" alt="vinti4" className="h-8 w-auto" data-testid="logo-vinti4" />
                <img src="/payment-logos/visa.png" alt="Visa Secure" className="h-7 w-auto" data-testid="logo-visa" />
                <img src="/payment-logos/mastercard.png" alt="Mastercard ID Check" className="h-7 w-auto" data-testid="logo-mastercard" />
                <img src="/payment-logos/amex.png" alt="American Express SafeKey" className="h-7 w-auto" data-testid="logo-amex" />
              </div>
            </div>
            <div className="text-xs text-gray-500 sm:text-right">
              <div>{t('checkout.secureConnection')}</div>
              <div className="mt-1">
                <a href="/politica-devolucoes" className="text-blue-600 hover:underline" data-testid="link-refund-policy">
                  {t('checkout.refundPolicyLink')}
                </a>
                {' · '}
                <a href="mailto:info@trabadju.cv" className="text-blue-600 hover:underline">
                  {t('checkout.customerSupport')}
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Summary */}
        {selectedPlan && (
          <Card className="p-6 bg-gray-900 text-white mb-6" data-testid="checkout-summary">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <div className="text-sm text-gray-400">{t('checkout.selectedPlan')}</div>
                <div className="text-xl font-bold">{selectedPlan[`name_${i18n.language}`] || selectedPlan.name_pt}</div>
              </div>
              <div className="text-right">
                <div className="text-sm text-gray-400">{t('checkout.totalToPay')}</div>
                {quote && quote.discount_cve > 0 && (
                  <div className="text-sm text-gray-400 line-through" data-testid="checkout-base-price">
                    {quote.base_amount_cve.toLocaleString('pt-PT')} CVE
                  </div>
                )}
                <div className="text-3xl font-bold" data-testid="checkout-final-price">
                  {(quote ? quote.amount_cve : selectedPlan.price_cve).toLocaleString('pt-PT')} <span className="text-sm text-gray-400">CVE</span>
                </div>
                <EurHint cve={quote ? quote.amount_cve : selectedPlan.price_cve} className="text-gray-300" testId="checkout-final-price-eur" />
              </div>
            </div>

            {quote && quote.applied_offers.length > 0 && (
              <ul className="mt-4 pt-4 border-t border-gray-700 space-y-1.5 text-sm" data-testid="checkout-applied-offers">
                {quote.applied_offers.map((o) => (
                  <li key={o.id} className="flex items-center justify-between gap-3">
                    <span className="flex items-center gap-2 text-emerald-300">
                      <Tag className="h-4 w-4" />
                      {o.name}
                      <span className="text-[10px] uppercase tracking-wider text-gray-400">
                        {t(`checkout.offerSource.${o.source}`)}
                      </span>
                    </span>
                    <span className="font-semibold text-emerald-300">
                      {o.kind === 'bonus_credits'
                        ? `+${o.bonus_credits} ${t('checkout.promoBonusShort')}`
                        : `−${o.saving_cve.toLocaleString('pt-PT')} CVE`}
                    </span>
                  </li>
                ))}
                {quote.bonus_credits > 0 && (
                  <li className="text-xs text-gray-400 pt-1" data-testid="checkout-bonus-credits">
                    {t('checkout.bonusCreditsHint', { count: quote.bonus_credits, total: selectedPlan.total_credits + quote.bonus_credits })}
                  </li>
                )}
              </ul>
            )}

            <div className="mt-4 pt-4 border-t border-gray-700">
              <label className="text-xs uppercase tracking-widest text-gray-400 block mb-2" htmlFor="promo-code-input">
                {t('checkout.promoCodeLabel')}
              </label>
              <div className="flex gap-2 flex-wrap">
                <input
                  id="promo-code-input"
                  value={promoCode}
                  onChange={(e) => { setPromoCode(e.target.value.toUpperCase()); setQuoteError(''); }}
                  placeholder={t('checkout.promoCodePlaceholder')}
                  className="flex-1 min-w-[160px] h-10 px-3 rounded-md bg-gray-800 border border-gray-700 text-white placeholder:text-gray-500 font-mono uppercase focus:outline-none focus:ring-2 focus:ring-emerald-400"
                  data-testid="promo-code-input"
                />
                {appliedCode ? (
                  <Button type="button" variant="outline" onClick={() => { setAppliedCode(''); setPromoCode(''); }} className="bg-gray-700 border-gray-600 text-white hover:bg-gray-600 hover:text-white" data-testid="promo-code-remove-btn">
                    {t('checkout.promoRemove')}
                  </Button>
                ) : (
                  <Button type="button" onClick={() => { setAutoApplied(false); setAppliedCode(promoCode.trim()); }} disabled={!promoCode.trim() || quoting} className="bg-emerald-500 hover:bg-emerald-600 text-gray-900 font-semibold" data-testid="promo-code-apply-btn">
                    {quoting ? '…' : t('checkout.promoApply')}
                  </Button>
                )}
              </div>
              {quoteError && <p className="text-sm text-rose-300 mt-2" data-testid="promo-code-error">{quoteError}</p>}
              {appliedCode && quote?.code_valid && quote.code_applied && (
                <p className="text-sm text-emerald-300 mt-2" data-testid="promo-code-success">{t('checkout.promoApplied', { code: appliedCode })}</p>
              )}
              {appliedCode && quote?.code_valid && quote.code_applied === false && (
                <p className="text-sm text-amber-300 mt-2" data-testid="promo-code-outbid">{t('checkout.promoOutbid')}</p>
              )}
            </div>
          </Card>
        )}

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <label
            className="flex items-start gap-3 cursor-pointer text-sm text-slate-700 max-w-xl"
            data-testid="terms-consent-label"
          >
            <input
              type="checkbox"
              checked={termsAccepted}
              onChange={(e) => setTermsAccepted(e.target.checked)}
              className="mt-1 h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
              data-testid="terms-consent-checkbox"
            />
            <span>
              {t('checkout.termsPrefix')}{' '}
              <a href="/termos" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">
                {t('checkout.termsLink')}
              </a>
              {' '}{t('checkout.termsAnd')}{' '}
              <a href="/politica-devolucoes" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">
                {t('checkout.refundPolicyLinkFull')}
              </a>
              .
            </span>
          </label>
          <Button
            onClick={handleConfirmPlan}
            disabled={!selected || submitting || !termsAccepted}
            size="lg"
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="checkout-confirm-btn"
          >
            {submitting ? t('checkout.processing') : t('checkout.confirmAndPay')}
          </Button>
        </div>
      </div>
    </div>
  );
}

function PaymentMethodCard({ active, disabled, onClick, icon: Icon, title, subtitle, badge, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`text-left p-5 rounded-xl border-2 transition-all flex items-start gap-4 ${
        disabled ? 'opacity-50 cursor-not-allowed bg-gray-50' :
        active ? 'border-blue-600 bg-blue-50' : 'border-gray-200 hover:border-gray-400 bg-white'
      }`}
      data-testid={testId}
    >
      <div className={`p-3 rounded-lg ${active ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'}`}>
        <Icon className="h-6 w-6" />
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <span className="font-semibold">{title}</span>
          {badge && <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full font-medium">{badge}</span>}
        </div>
        <div className="text-sm text-gray-600 mt-1">{subtitle}</div>
      </div>
    </button>
  );
}

function DataRow({ label, value, placeholder, highlight, onCopy, copied, t }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-blue-100 last:border-b-0">
      <span className="text-sm text-gray-600">{label}</span>
      <div className="flex items-center gap-2">
        <span className={`font-mono text-sm ${highlight ? 'font-bold text-blue-700' : placeholder ? 'text-gray-400 italic' : 'text-gray-900'}`}>
          {value}
        </span>
        {onCopy && (
          <button onClick={onCopy} className="text-gray-400 hover:text-blue-600" title={t('checkout.copy')}>
            {copied ? <Check className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
          </button>
        )}
      </div>
    </div>
  );
}
